import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { Module } from 'src/app/_models/module-enum';
import { DeviceAdminSummary } from 'src/app/_models/device';
import { DeviceSetupStatus } from 'src/app/_models/device-setup-status';
import { DeviceService } from 'src/app/_services/device.service';
import { ModalService } from 'src/app/_services/modal.service';
import { clearFormArray } from 'src/app/_helpers/utils';
import { ActiveModulesComponent } from 'src/app/_components/chips/active-modules/active-modules.component';
import { DropdownFormFieldComponent } from 'src/app/_components/forms/dropdown-form-field/dropdown-form-field.component';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { IconButtonComponent } from 'src/app/_components/buttons/icon-button/icon-button.component';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';
import { FilterModalComponent } from 'src/app/_components/modals/filter-modal/filter-modal.component';
import { SearchFieldComponent } from 'src/app/_components/forms/search-field/search-field.component';
import { SoftwareStatusIconComponent } from 'src/app/_components/icons/software-status-icon/software-status-icon.component';
import { StatusIndicatorComponent } from 'src/app/_components/chips/status-indicator/status-indicator.component';
import { ToastComponent } from 'src/app/_components/toast/toast.component';
import { MatIcon } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-administration-devices',
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ActiveModulesComponent,
    DropdownFormFieldComponent,
    ErrorPageComponent,
    FilterModalComponent,
    IconButtonComponent,
    LoadingSpinnerComponent,
    MatIcon,
    MatTableModule,
    MatTooltip,
    SearchFieldComponent,
    SoftwareStatusIconComponent,
    StatusIndicatorComponent,
    ToastComponent,
  ],
})
export class AdministrationDevicesComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    'deviceName',
    'organisation',
    'laboratory',
    'status',
    'installedModules',
    'macAddress',
  ];

  private readonly deviceService = inject(DeviceService);
  private readonly modalService = inject(ModalService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();
  private toastTimeout?: ReturnType<typeof setTimeout>;

  private pageRequested = 1;
  private pageSize = 5;

  readonly DeviceSetupStatus = DeviceSetupStatus;

  selectedModuleList: Module[] = [];

  devices = signal<DeviceAdminSummary[]>([]);
  isFirstLoad = signal(true);
  error = signal<ErrorObject>(null);
  currentSort = signal({ sortBy: 'Name', sortDirection: 'asc' });
  currentPage = signal(1);
  totalPages = signal(0);
  hasNext = signal(false);
  hasPrevious = signal(false);
  hasActiveFilters = signal(false);

  toastMessage = signal<string | null>(null);
  toastStyle = signal<'positive' | 'critical'>('positive');
  toastIcon = signal<string>('checkmark');
  displayToast = signal(false);

  searchField = new FormBase<string>({
    key: 'search',
    label: 'Search',
    type: 'text',
    placeholder: 'Search...',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  devicesPerPageField = new FormBase({
    key: 'devicesPerPage',
    label: 'Devices Per Page',
    type: 'dropdown',
    placeholder: '',
    disabled: false,
    required: false,
    value: '5',
    options: [
      { key: '5', value: '5' },
      { key: '10', value: '10' },
      { key: '25', value: '25' },
      { key: '50', value: '50' },
      { key: '100', value: '100' },
    ],
  });

  moduleFiltersField = new FormBase({
    key: 'moduleFilters',
    label: 'Installed modules:',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      { key: Module.Fluorescence, value: 'Fluorescence' },
      { key: Module.LiquidControl, value: 'Liquid Control' },
      { key: Module.Oxygen, value: 'Oxygen' },
      { key: Module.pH, value: 'pH' },
      { key: Module.Sparging, value: 'Sparging' },
    ],
  });

  includeDeletedField = new FormBase<boolean>({
    key: 'includeDeleted',
    label: 'Include deleted devices',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: null,
    options: [],
  });

  tableControls = this.formBuilder.group({
    [this.searchField.key]: new FormControl(this.searchField.value),
    [this.devicesPerPageField.key]: new FormControl(this.devicesPerPageField.value),
    [this.moduleFiltersField.key]: new FormArray([]),
    [this.includeDeletedField.key]: new FormControl(this.includeDeletedField.value),
  });

  get checkboxesFormArray(): FormArray {
    return this.tableControls.get(this.moduleFiltersField.key) as FormArray;
  }

  ngOnInit(): void {
    this.initialiseCheckboxes();

    this.tableControls
      .get(this.devicesPerPageField.key)
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.pageSize = Number(value);
        this.pageRequested = 1;
        this.getDevices();
      });

    this.getDevices();
  }

  getDevices(): void {
    const search = this.tableControls.controls['search'].value as string;
    const includeDeleted = this.tableControls.controls[this.includeDeletedField.key]
      .value as boolean;
    const sort = this.currentSort();

    this.deviceService
      .getAllDevices(
        sort.sortBy,
        sort.sortDirection,
        this.pageRequested,
        this.pageSize,
        this.selectedModuleList.length > 0 ? this.selectedModuleList : undefined,
        includeDeleted ?? undefined,
        typeof search === 'string' && search ? search : undefined,
      )
      .subscribe({
        next: (result) => {
          this.error.set(null);
          this.devices.set(result.devices);
          this.currentPage.set(result.paginationData.CurrentPage);
          this.totalPages.set(result.paginationData.TotalPages);
          this.hasNext.set(result.paginationData.HasNext);
          this.hasPrevious.set(result.paginationData.HasPrevious);
          this.isFirstLoad.set(false);
        },
        error: (err: ErrorObject) => {
          if (this.isFirstLoad()) {
            // Initial load failed: there is no table to fall back to, so show
            // the full-page error state.
            this.error.set(err);
            this.isFirstLoad.set(false);
          } else {
            // A pagination/sort/search/filter request failed: keep the
            // already-rendered table and surface the failure via a toast.
            this.showToast('Failed to load devices', 'critical', 'warning');
          }
        },
      });
  }

  onSort(sortTerm: string): void {
    const current = this.currentSort();
    if (current.sortBy === sortTerm) {
      this.currentSort.set({
        ...current,
        sortDirection: current.sortDirection === 'asc' ? 'desc' : 'asc',
      });
    } else {
      this.currentSort.set({ sortBy: sortTerm, sortDirection: 'asc' });
    }
    this.pageRequested = 1;
    this.getDevices();
  }

  handleSearch(action: string): void {
    if (action === 'clear-search') {
      this.tableControls.patchValue({ search: null });
    }
    this.pageRequested = 1;
    this.getDevices();
  }

  getPrevious(): void {
    this.pageRequested = this.currentPage() - 1;
    this.getDevices();
  }

  getNext(): void {
    this.pageRequested = this.currentPage() + 1;
    this.getDevices();
  }

  moduleCheckboxClicked(event: Event): void {
    const target = event.target as HTMLInputElement;
    const module = parseInt(target.name) as Module;
    if (target.checked) {
      this.selectedModuleList.push(module);
    } else {
      this.selectedModuleList = this.selectedModuleList.filter((m) => m !== module);
    }
  }

  initialiseCheckboxes(): void {
    clearFormArray(this.checkboxesFormArray);
    this.moduleFiltersField.options.forEach(() => {
      this.checkboxesFormArray.push(this.formBuilder.control(false));
    });
  }

  filterModalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'continue-button':
        this.hasActiveFilters.set(
          this.selectedModuleList.length > 0 ||
            !!this.tableControls.controls[this.includeDeletedField.key].value,
        );
        this.pageRequested = 1;
        this.getDevices();
        this.closeModal('filter');
        break;
      case 'reset-button':
        this.clearFilters();
        break;
    }
  }

  clearFilters(): void {
    this.selectedModuleList = [];
    this.tableControls.patchValue({ [this.includeDeletedField.key]: null });
    this.checkboxesFormArray.patchValue(
      this.checkboxesFormArray.controls.map(() => false),
    );
    this.hasActiveFilters.set(false);
    this.pageRequested = 1;
    this.getDevices();
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  showToast(
    message: string,
    toastStyle: 'positive' | 'critical' = 'positive',
    icon = 'checkmark',
  ): void {
    this.toastMessage.set(message);
    this.toastStyle.set(toastStyle);
    this.toastIcon.set(icon);
    this.displayToast.set(true);
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
    this.toastTimeout = setTimeout(() => {
      this.displayToast.set(false);
    }, 5000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
  }
}
